import React from 'react';
import {
  ActionIcon, Button, Group, NumberInput, SegmentedControl, Select, Stack, Text,
} from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import i18n from '../../../../i18n';
import { cellStyleEntry, ROW_BORDER_SIDES } from '../../../../shared/tables/tableRowStyle';
import { PuckSectionHeading } from '../../../puck/fields/puckSectionField';
import InspectorRow from '../../../puck/fields/InspectorRow';
import { PuckColorFieldWithPalette } from '../../../puck/fields/createPuckColorField';
import TextStyleToggles, { TextAlignButtons } from '../../../puck/fields/TextStyleToggles';
import { usePuckRichTextStyle } from '../../../context/PuckRichTextStyleContext';

// Alignements d'une règle (ceux des colonnes du catalogue) ↔ boutons du site.
const ALIGN_TO_BUTTON = { start: 'left', center: 'center', end: 'right' };
const BUTTON_TO_ALIGN = { left: 'start', center: 'center', right: 'end' };

// Mêmes icônes et même ordre que le panel du classique (TableRowStylePanel).
const BORDER_SIDE_ICONS = {
  all: icon({ name: 'border-outer', style: 'light' }),
  left: icon({ name: 'border-left', style: 'light' }),
  top: icon({ name: 'border-top', style: 'light' }),
  right: icon({ name: 'border-right', style: 'light' }),
  bottom: icon({ name: 'border-bottom', style: 'light' }),
};

// « Transparent » ou vide : pas de couleur propre, celle du tableau.
const ownColor = (value) => (value && value !== 'transparent' ? value : null);

/**
 * « Style de la ligne » : le pendant web du panel du classique
 * (TableRowStylePanel), dans la sidebar tant que l'écran « Configurer le
 * tableau » est ouvert. Il règle la ligne cliquée dans l'aperçu — toutes les
 * lignes de son type ou de son libellé — avec les mêmes règles
 * (shared/tables/tableRowStyle) ; le bloc les rend comme le document.
 *
 * `row` : `{ label, kind }` ou null ; `rule` : sa règle ; `column` : libellé
 * d'origine de la case cliquée, `scope` ('row' | 'column') la portée des
 * réglages — en portée colonne, pas de traits (partagés entre deux cases).
 * `tableFont` : `{ family, size }` du tableau, montrés tant que la ligne n'a
 * pas les siens.
 */
export default function TableRowStyleSection({
  row, rule: rowRule, column, scope = 'row', tableFont, onScopeChange, onChange, onReset,
}) {
  const { fontFamilies } = usePuckRichTextStyle();
  const columnScope = scope === 'column' && column != null;
  // En portée colonne, les contrôles montrent ce que la case affiche.
  const rule = columnScope ? { ...rowRule, ...cellStyleEntry(rowRule, column) } : rowRule;

  const colorRow = (field, labelKey) => (
    <InspectorRow key={field} label={i18n.t(labelKey)}>
      <PuckColorFieldWithPalette
        label={null}
        value={rule?.[field] || ''}
        onChange={(value) => onChange({ [field]: ownColor(value) })}
        allowTransparent
        emptyIsInherited
      />
    </InspectorRow>
  );

  const hidden = rule?.hiddenBorders ?? [];
  const toggleBorder = (side) => {
    const checked = side === 'all' ? !hidden.length : !hidden.includes(side);
    const next = side === 'all'
      ? (checked ? [...ROW_BORDER_SIDES] : [])
      : (checked ? [...hidden, side] : hidden.filter((s) => s !== side));
    onChange({ hiddenBorders: next });
  };

  return (
    <Stack gap="xs">
      <PuckSectionHeading label={i18n.t('table-row-style')} collapsible={false} />
      {!row ? (
        <Text size="xs" c="dimmed">{i18n.t('table-row-style-hint')}</Text>
      ) : (
        <>
          <Text size="xs" c="dimmed">
            {row.kind ? i18n.t(`table-merge-kind-${row.kind}`) : i18n.t('table-label-row-caption', { label: row.label })}
          </Text>
          {column != null && (
            <>
              <SegmentedControl
                size="xs"
                fullWidth
                value={columnScope ? 'column' : 'row'}
                onChange={onScopeChange}
                data={[
                  { value: 'row', label: i18n.t('table-row-scope-row') },
                  { value: 'column', label: i18n.t('table-row-scope-column') },
                ]}
              />
              {columnScope && (
                <Text size="xs" c="dimmed">{i18n.t('table-row-scope-column-note', { column })}</Text>
              )}
            </>
          )}
          <Group justify="space-between" wrap="nowrap">
            <TextStyleToggles
              active={rule || {}}
              onToggle={(field) => onChange({ [field]: !rule?.[field] })}
            />
            {/* Un nouveau clic sur l'alignement choisi rend celui des colonnes. */}
            <TextAlignButtons
              value={ALIGN_TO_BUTTON[rule?.align]}
              options={['left', 'center', 'right']}
              onChange={(key) => onChange({
                align: rule?.align === BUTTON_TO_ALIGN[key] ? null : BUTTON_TO_ALIGN[key],
              })}
            />
          </Group>
          <Group grow wrap="nowrap" gap="xs">
            {fontFamilies.length > 1 && (
              <Select
                size="xs"
                aria-label={i18n.t('font-family')}
                data={fontFamilies}
                value={rule?.fontFamily ?? tableFont?.family ?? null}
                onChange={(fontFamily) => onChange({ fontFamily })}
                allowDeselect={false}
                comboboxProps={{ withinPortal: true }}
              />
            )}
            <NumberInput
              size="xs"
              aria-label={i18n.t('font-size')}
              min={6}
              max={96}
              suffix=" px"
              value={rule?.fontSize ?? tableFont?.size ?? ''}
              onChange={(value) => onChange({ fontSize: value === '' ? null : Number(value) })}
            />
          </Group>
          {colorRow('color', 'font-color')}
          {colorRow('background', 'background-color')}
          {!columnScope && (
            <>
              {colorRow('borderColor', "border's-color")}
              {/* Un trait retiré l'est des deux lignes qui le partagent. */}
              <InspectorRow label={i18n.t('border')}>
                <Group gap={4} wrap="nowrap">
                  {['all', 'left', 'top', 'right', 'bottom'].map((side) => {
                    const checked = side === 'all' ? !hidden.length : !hidden.includes(side);
                    return (
                      <ActionIcon
                        key={side}
                        size="sm"
                        variant={checked ? 'light' : 'default'}
                        color="editor"
                        aria-label={i18n.t(`table-row-border-${side}`)}
                        title={i18n.t(`table-row-border-${side}`)}
                        aria-pressed={checked}
                        onClick={() => toggleBorder(side)}
                      >
                        <FontAwesomeIcon icon={BORDER_SIDE_ICONS[side]} style={{ fontSize: 14 }} />
                      </ActionIcon>
                    );
                  })}
                </Group>
              </InspectorRow>
            </>
          )}
          <Button
            size="compact-xs"
            variant="default"
            fullWidth
            disabled={columnScope ? !cellStyleEntry(rowRule, column) : !rowRule}
            onClick={onReset}
          >
            {i18n.t('table-row-style-reset')}
          </Button>
        </>
      )}
    </Stack>
  );
}
